/**
 * Outfitting tab (spec §7.3, §8): the 9 slots with the fitted Mk and power draw, the power bar, and for the
 * selected slot the offers (fit from the locker, buy, sell from the locker at 60%) with the stat-diff preview of
 * the focused offer. Refusals (funds, power budget) come back inline from the commands.
 * Recondition (economy plan SY-5b): a Serviceable hold component gets a "Recondition" button with its goods bill
 * (paid in goods only); a short hold disables it and names what is missing.
 * Salvaged (economy plan SY-5c): Intact hold components install as modules, and salvaged modules fit or sell at
 * component value. Its buttons, the Recondition buttons and the Scrap buttons (MC-c7: a locker module into materials
 * in the hold, `craft { kind: 'module.scrap' }`) form the `G.salvaged` focus group (below the slots).
 * Fabricate (M8 plan MC-c5): Mk II/III offers with a recipe end the slot's offers (after fit and sell), then each open
 * fab order here (Deliver, Give salvage, Buy missing, Complete, Cancel). The preview of a fab offer shows the credits,
 * the materials (need, delivered, hold, berth, the berths in reach when remote pull draws on them, what the station
 * sells now, the carry), the salvaged alternative and the shortage card. Every fab action is a `UiActions.craft` command (`features/fab/feature.ts`).
 * Patterns (M8 plan MC-f4): after every other offer, one row per faction-pattern variant of the slot. Unlocked, it is
 * a Fabricate offer; locked, a Study offer (`craft { kind: 'pattern.study' }`, `features/craftStudy`), enabled only
 * with enough Intact qualifying parts in the hold, whose preview shows the pattern id and every unlock route.
 * A disabled offer (PF-5: `enabled` false, such as Mk III Buy or Fabricate outside a fabworks or yards system) carries
 * its reason as the button hint and in the preview.
 */
import type { ReactNode } from 'react'
import type { SlotId } from '../../data/m2Schemas'
import { FocusButton, Meter, useFocusable } from '../focus'
import { G } from '../models/layout'
import { FAB_TEXT, fabBuyLabel, type PatternPreview, salvagedButtonCount, type FabPreview, type ModuleOffer, type QuoteGood, type OutfittingModel, type ReconditionRow, type SalvagedCommand, type ScrapRow, type SalvagedRow, type SlotRow } from '../models/outfitting'
import type { CraftCommand } from '../../craft/hook'
import type { ScreenProps } from './common'

function SlotLine(p: { row: SlotRow; index: number; selected: boolean }): ReactNode {
  const f = useFocusable(G.slots, p.index, {})
  return (
    <div className="ui-row ui-row--slot" data-selected={p.selected ? '' : undefined} data-testid={`outfit-slot-${p.row.slot}`} {...f.props}>
      <span className="ui-row__main">
        <span className="ui-row__sub">{p.row.label}</span>
        <span className="ui-row__title">{p.row.moduleName}</span>
      </span>
      <span className="ui-mk" aria-label={`Mark ${p.row.mk}`}>
        {'◆'.repeat(p.row.mk)}
        <span className="ui-mk__off">{'◇'.repeat(3 - p.row.mk)}</span>
      </span>
      <span className="ui-row__price" title="Power">
        {'⚡'} {p.row.powerLabel}
      </span>
    </div>
  )
}

/** The craft command of a fab offer (`fab.*`), or null when the offer carries no fab preview. */
function fabCommand(o: ModuleOffer): CraftCommand | null {
  const f = o.fab
  if (f === undefined) return null
  if (o.action === 'fabricate') return { kind: 'fab.start', stationId: f.stationId, moduleId: f.moduleId }
  const orderId = f.orderId
  if (orderId === null) return null
  switch (o.action) {
    case 'deliver':
      return { kind: 'fab.deliver', orderId }
    case 'giveSalvage':
      return f.salvageId === null ? null : { kind: 'fab.deliver', orderId, salvage: f.salvageId }
    case 'buyMissing':
      return { kind: 'fab.deliver', orderId, buyMissing: true }
    case 'complete':
      return { kind: 'fab.complete', orderId }
    case 'cancel':
      return { kind: 'fab.cancel', orderId }
    default:
      return null
  }
}

function offerAction(ctl: ScreenProps['ctl'], slot: SlotId, o: ModuleOffer): () => void {
  switch (o.action) {
    case 'fabricate':
    case 'deliver':
    case 'giveSalvage':
    case 'buyMissing':
    case 'complete':
    case 'cancel': {
      const craft = ctl.actions.craft
      const cmd = fabCommand(o)
      return craft === undefined || cmd === null ? noop : () => ctl.run(() => craft(cmd))
    }
    case 'fit':
      return () => ctl.run(() => ctl.actions.fitModule(slot, o.id))
    case 'buy':
      return () => ctl.run(() => ctl.actions.buyModule(o.id))
    case 'sell':
      return () => ctl.run(() => ctl.actions.sellModule(o.id))
    case 'study': {
      const craft = ctl.actions.craft
      const s = o.pattern?.study ?? null
      return craft === undefined || s === null ? noop : () => ctl.run(() => craft({ kind: 'pattern.study', pattern: s.pattern, rung: s.rung }))
    }
  }
}

/** The pattern part of a variant's preview (MC-f4): its id, unlock state and routes. */
function PatternDetail(p: { pattern: PatternPreview }): ReactNode {
  const v = p.pattern
  return (
    <div className="ui-fab ui-pattern" data-testid="outfit-pattern-preview" data-unlocked={v.unlocked ? '1' : '0'}>
      <p className="ui-row__sub">{v.idLine}</p>
      <p className="ui-row__title">{v.status}</p>
      {v.routes.map((r) => (
        <p key={r.route} className="ui-row__sub" data-testid={`outfit-pattern-route-${r.route}`} data-met={r.met ? '1' : '0'}>
          {r.met ? '✓' : '·'} {r.text}
        </p>
      ))}
      {v.hint !== null ? <p className="ui-row__sub">{v.hint}</p> : null}
      <p className="ui-row__sub">{v.note}</p>
    </div>
  )
}

/** The materials of a fab offer: credits, the material table, the salvaged alternative and the shortage card. */
function FabDetail(p: { fab: FabPreview; quoteGood: QuoteGood | undefined }): ReactNode {
  const f = p.fab
  const order = f.orderId !== null
  // MC-c5b: the column shows only when a line draws on the player's other berths in reach (remote pull).
  const remote = f.lines.some((l) => l.remote !== '')
  return (
    <div className="ui-fab" data-testid="outfit-fab-preview">
      <p className="ui-row__sub">{f.summary}</p>
      <table className="ui-diff ui-fab__lines">
        <thead>
          <tr>
            <th scope="col">{FAB_TEXT.colItem}</th>
            <th scope="col">{FAB_TEXT.colNeed}</th>
            {order ? <th scope="col">{FAB_TEXT.colHave}</th> : null}
            <th scope="col">{FAB_TEXT.colHold}</th>
            <th scope="col">{FAB_TEXT.colBerth}</th>
            {remote ? <th scope="col">{FAB_TEXT.colRemote}</th> : null}
            <th scope="col">{FAB_TEXT.colBuy}</th>
          </tr>
        </thead>
        <tbody>
          {f.lines.map((l) => (
            <tr key={l.id} data-testid={`outfit-fab-line-${l.id}`} data-missing={l.missing > 0 ? '1' : '0'}>
              <th scope="row">
                {l.name}
                {l.carry ? <span className="ui-row__sub"> ({FAB_TEXT.carryTag})</span> : null}
              </th>
              <td>{l.need}</td>
              {order ? <td>{l.have}</td> : null}
              <td>{l.hold}</td>
              <td>{l.berth}</td>
              {remote ? <td data-testid={`outfit-fab-remote-${l.id}`}>{l.remote === '' ? FAB_TEXT.noBuy : l.remote}</td> : null}
              <td>{fabBuyLabel(l, p.quoteGood)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {f.ready ? <p className="ui-row__sub">{FAB_TEXT.ready}</p> : null}
      {f.salvage !== null ? <p className="ui-row__sub">{f.salvage}</p> : null}
      {f.shortage.map((c) => (
        <div key={c.item} className="ui-fab__shortage" data-testid={`outfit-fab-shortage-${c.item}`}>
          <p className="ui-row__title">{c.title}</p>
          {c.hint !== null ? <p className="ui-row__sub">{c.hint}</p> : null}
          {c.rows.map((r) => (
            <p key={r} className="ui-row__sub">
              {r}
            </p>
          ))}
        </div>
      ))}
      <p className="ui-row__sub">{f.note}</p>
    </div>
  )
}

function Preview(p: { offer: ModuleOffer | undefined; quoteGood: QuoteGood | undefined }): ReactNode {
  const o = p.offer
  if (o === undefined) return <p className="ui-empty">Nothing to fit, buy or sell for this slot here.</p>
  return (
    <div className="ui-preview" aria-live="polite">
      <h4 className="ui-preview__title">{o.name}</h4>
      {o.owned > 0 ? <p className="ui-row__sub">In your locker: {o.owned}</p> : null}
      {o.warning !== null ? (
        <p className="ui-warning" role="note">
          {'⚠'} {o.warning}
        </p>
      ) : null}
      {o.pattern !== undefined ? <PatternDetail pattern={o.pattern} /> : null}
      {o.fab !== undefined ? <FabDetail fab={o.fab} quoteGood={p.quoteGood} /> : null}
      {o.diff.length === 0 ? (
        o.fab !== undefined || o.pattern !== undefined ? null : <p className="ui-row__sub">{o.action === 'sell' ? 'Sold from the locker; your fit does not change.' : 'No change to your fit.'}</p>
      ) : (
        <table className="ui-diff">
          <tbody>
            {o.diff.map((d) => (
              <tr key={d.stat} data-better={d.better ? '1' : '0'}>
                <th scope="row">{d.label}</th>
                <td>{d.before}</td>
                <td className="ui-diff__arrow">{d.better ? '▲' : '▼'}</td>
                <td>{d.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** The `UiActions` call of a Salvaged button, or undefined when the runtime lacks it. */
function salvagedAction(ctl: ScreenProps['ctl'], cmd: SalvagedCommand): (() => void) | undefined {
  const a = ctl.actions
  switch (cmd.action) {
    case 'install': {
      const install = a.installComponent
      return install === undefined ? undefined : () => ctl.run(() => install(cmd.id, cmd.kind))
    }
    case 'fit': {
      const fit = a.fitSalvaged
      return fit === undefined ? undefined : () => ctl.run(() => fit(cmd.slot, cmd.sourceId))
    }
    case 'sell': {
      const sell = a.sellSalvaged
      return sell === undefined ? undefined : () => ctl.run(() => sell(cmd.sourceId))
    }
  }
}

function noop(): void {}

function Salvaged(p: { rows: readonly SalvagedRow[]; ctl: ScreenProps['ctl'] }): ReactNode {
  if (p.rows.length === 0) return null
  let index = 0
  return (
    <section className="ui-section" data-testid="outfit-salvaged">
      <h3 className="ui-section__title">Salvaged</h3>
      {p.rows.map((r) => (
        <div key={r.id} className="ui-row-group" data-testid={r.testId}>
          <p className="ui-row__main">
            <span className="ui-row__title">{r.name}</span>
            <span className="ui-row__sub">
              {r.where} · {r.source}
            </span>
            <span className="ui-row__sub" data-testid={`${r.testId}-resale`}>
              {r.resale}
            </span>
          </p>
          {r.buttons.map((b) => {
            const run = salvagedAction(p.ctl, b.cmd)
            return (
              <FocusButton
                key={b.testId}
                group={G.salvaged}
                index={index++}
                testId={b.testId}
                className="ui-btn--small"
                disabled={!b.enabled || run === undefined}
                title={b.warning ?? undefined}
                onActivate={run ?? noop}
              >
                {b.label}
              </FocusButton>
            )
          })}
        </div>
      ))}
    </section>
  )
}

/** Recondition rows (SY-5b); their buttons follow the Salvaged buttons in `G.salvaged` from `first` (SY-5c). */
function Recondition(p: { rows: readonly ReconditionRow[]; ctl: ScreenProps['ctl']; first: number }): ReactNode {
  const recondition = p.ctl.actions.recondition
  if (p.rows.length === 0) return null
  return (
    <section className="ui-section" data-testid="outfit-recondition">
      <h3 className="ui-section__title">Recondition</h3>
      {p.rows.map((r, i) => (
        <div key={r.id} className="ui-row-group">
          <FocusButton
            group={G.salvaged}
            index={p.first + i}
            testId={r.testId}
            className="ui-btn--small"
            disabled={!r.enabled || recondition === undefined}
            title={r.warning ?? undefined}
            onActivate={recondition === undefined ? noop : () => p.ctl.run(() => recondition(r.id))}
          >
            {r.label}
          </FocusButton>
          {r.warning !== null ? (
            <p className="ui-warning" role="note">
              {'⚠'} {r.warning}
            </p>
          ) : null}
        </div>
      ))}
    </section>
  )
}

/**
 * Scrap rows (MC-c7, D-526): each locker module with what scrapping returns; the button runs `craft { kind:
 * 'module.scrap' }` (`features/scrap`). Its buttons follow the Recondition buttons in `G.salvaged` from `first`.
 */
function Scrap(p: { rows: readonly ScrapRow[]; ctl: ScreenProps['ctl']; first: number }): ReactNode {
  const craft = p.ctl.actions.craft
  if (p.rows.length === 0) return null
  return (
    <section className="ui-section" data-testid="outfit-scrap">
      <h3 className="ui-section__title">Scrap</h3>
      {p.rows.map((r, i) => (
        <div key={r.id} className="ui-row-group">
          <FocusButton
            group={G.salvaged}
            index={p.first + i}
            testId={r.testId}
            className="ui-btn--small"
            disabled={!r.enabled || craft === undefined}
            title={r.warning ?? undefined}
            onActivate={craft === undefined ? noop : () => p.ctl.run(() => craft(r.cmd))}
          >
            {r.label}
          </FocusButton>
          {r.warning !== null ? (
            <p className="ui-warning" role="note">
              {'⚠'} {r.warning}
            </p>
          ) : null}
        </div>
      ))}
    </section>
  )
}

export function OutfittingTab(p: ScreenProps & { model: OutfittingModel; slot: SlotId; offers: ModuleOffer[]; focusedOffer: number }): ReactNode {
  const { model, ctl, slot, offers } = p
  const power = model.power
  return (
    <div className="ui-tab ui-outfit">
      <div className="ui-power">
        <span className="ui-power__label">Power</span>
        <Meter value={power.fraction} label={`${power.label}${power.over ? ' over budget' : ''}`} tone={power.over ? 'danger' : power.fraction > 0.9 ? 'warning' : 'ok'} />
      </div>
      {model.frame !== null ? (
        <p className="ui-seed ui-seed--paper" data-testid="outfit-frame">
          {model.frame}
        </p>
      ) : null}
      <div className="ui-split">
        <section className="ui-section ui-split__list">
          <h3 className="ui-section__title">Slots</h3>
          {model.slots.map((row, i) => (
            <SlotLine key={row.slot} row={row} index={i} selected={row.slot === slot} />
          ))}
        </section>
        <section className="ui-section ui-split__detail">
          <h3 className="ui-section__title">{ctl.cfg.slots[slot]}</h3>
          {!model.available ? <p className="ui-empty">{model.stationName} has no outfitting service.</p> : null}
          <div className="ui-offers">
            {offers.map((o, i) => (
              <FocusButton
                key={`${o.action}:${o.id}`}
                group={G.offers}
                index={i}
                testId={o.testId}
                className={`ui-offer ui-offer--${o.action}`}
                disabled={!o.enabled}
                title={o.enabled ? undefined : (o.warning ?? undefined)}
                onActivate={offerAction(ctl, slot, o)}
              >
                <span>{o.label}</span>
                {o.warning !== null ? <span className="ui-tag" data-kind="warning">{'⚠'}</span> : null}
              </FocusButton>
            ))}
          </div>
          <Preview offer={offers[Math.min(p.focusedOffer, offers.length - 1)]} quoteGood={ctl.actions.quoteGood} />
        </section>
      </div>
      <Salvaged rows={model.salvaged} ctl={ctl} />
      <Recondition rows={model.recondition} ctl={ctl} first={salvagedButtonCount(model.salvaged)} />
      <Scrap rows={model.scrap} ctl={ctl} first={salvagedButtonCount(model.salvaged) + model.recondition.length} />
    </div>
  )
}
